import { Chip, Tooltip } from "@mui/material";
import type { Availability, AvailabilityState } from "@shared/content-rights";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import { useFormat, type Formatters } from "../../i18n/format";
import { translateKey, type Translate } from "../../i18n/translate";

/**
 * What a Distribution list says instead of "Published" (`BE-7`, `BE-15`).
 *
 * There is no publish state in this catalogue and there is never going to be
 * one: an asset is live the instant it is POSTed. The three things that looked
 * like one were all theatre - `CMSAsset.isPublished` in the legacy portal is a
 * hardcoded `return true` (its PUBLISHED column is always ticked),
 * `Media.enabled` is required on write and read by nothing, and
 * `ContentChannel.isPublic` filters a list while a non-public channel still
 * plays.
 *
 * So the column shows the one thing with storage, CRUD and an enforcement path
 * that actually runs: the rights window. Which means the honest states are five,
 * not two - and two of them are about what we KNOW rather than about the row:
 *
 *  - `unrestricted` - no rights row at all, which upstream means "available
 *    everywhere, forever". That is the import default and not a decision
 *    anybody made, so it is drawn differently from a licence somebody authored.
 *  - `unknown` - the caller passed something with no `contentRights` key. Never
 *    "fine" and never "broken".
 */

const COLORS: Record<AvailabilityState, string> = {
  unknown: tokens.inkFaint,
  unrestricted: tokens.inkMuted,
  available: tokens.success,
  scheduled: tokens.warning,
  expired: tokens.error,
};

function detail(
  t: Translate,
  fmt: Formatters,
  availability: Availability
): string {
  const k = (name: string, params?: Record<string, unknown>) =>
    translateKey(t, "content:availability.detail." + name, params);
  switch (availability.state) {
    case "unknown":
    case "unrestricted":
    case "expired":
      return k(availability.state);
    case "available":
      return availability.availableIn.length
        ? k("availableIn", {
            markets: availability.availableIn.join(", ").toUpperCase(),
          })
        : k("available");
    case "scheduled":
      return availability.nextChangeAt
        ? k("scheduledFrom", { at: fmt.dateTime(availability.nextChangeAt) })
        : k("scheduled");
    default:
      return "";
  }
}

export default function AvailabilityChip({
  availability,
  size = "small",
}: {
  availability: Availability;
  size?: "small" | "medium";
}) {
  const { t } = useTranslation("content");
  const fmt = useFormat();
  const state = availability.state;
  const next = availability.nextChangeAt;
  const title =
    detail(t, fmt, availability) +
    (next && state === "available"
      ? " " + t("availability.detail.changesAt", { at: fmt.dateTime(next) })
      : "");
  return (
    <Tooltip title={title}>
      <Chip
        size={size}
        variant="outlined"
        label={t(`availability.label.${state}`)}
        data-testid={`availability-${state}`}
        sx={{
          borderColor: COLORS[state],
          color: COLORS[state],
          fontWeight: "fontWeightSemibold",
          typography: "micro",
        }}
      />
    </Tooltip>
  );
}
